import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, MapPin, Star } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { VenuesGrid } from '@/components/venues/VenuesGrid';
import { useIsMobile } from '@/hooks/use-mobile';
import { venueRatingsService, type VenueRating } from '@/services/venueRatingsService';

interface Venue {
  id: string;
  venue_name: string;
  address: string;
  city: string;
  state_province: string;
  phone_number?: string;
  website?: string;
  operating_hours?: string;
  amenities?: string[];
  sports_available?: string[];
  rating?: number;
  price_range?: string;
  description?: string;
  source?: string;
  slug?: string;
  profile_photo_url?: string;
  hourly_rates?: number;
}

export default function VenueDiscovery() {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [ratingsMap, setRatingsMap] = useState<Record<string, VenueRating>>({});
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  useEffect(() => {
    loadVenues();
  }, []);

  const loadVenues = async () => {
    try {
      setLoading(true);
      console.log('🏢 Loading venues from both sources...');
      
      // Load venues from the venues table
      const { data: venuesData, error: venuesError } = await supabase
        .from('venues')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('🏢 Venues table data:', venuesData);
      
      // Load venues from internal_user_profiles where role = 'venue'
      const { data: internalVenuesData, error: internalError } = await supabase
        .from('internal_user_profiles')
        .select(`
          *,
          internal_users!inner(
            id,
            username,
            role
          )
        `)
        .eq('internal_users.role', 'venue');

      console.log('🏢 Internal venues data:', internalVenuesData);

      if (venuesError && internalError) {
        throw new Error('Failed to load venues from both sources');
      }

      let allVenues: Venue[] = [];

      // Transform venues table data
      if (venuesData) {
        const transformedVenues = venuesData.map(venue => {
          const fullAddress = venue.venue_address || venue.address_line || '';
          const addressParts = fullAddress.split(',').map(part => part.trim());
          
          // Clean address parsing to avoid duplication
          const displayAddress = venue.venue_address || venue.address_line || '';
          const displayCity = venue.city || '';
          const displayState = venue.state_province || '';
          
          return {
            id: `venue_${venue.venue_id}`,
            venue_name: venue.venue_name || 'Unknown Venue',
            address: displayAddress,
            city: displayCity,
            state_province: displayState,
            phone_number: venue.phone || '',
            website: venue.website || '',
            operating_hours: '',
            amenities: Array.isArray(venue.amenities) ? venue.amenities.map(String) : [],
            sports_available: [],
            rating: undefined, // Will be loaded separately
            price_range: '$$',
            description: venue.venue_description || venue.description || 'Great venue for sports activities',
            source: 'venues',
            profile_photo_url: ''
          } as Venue;
        });
        allVenues = [...allVenues, ...transformedVenues];
      }

      // Transform internal venues data
      if (internalVenuesData) {
        const calculatePriceRange = (hourlyRate?: number): string => {
          if (!hourlyRate) return '$';
          if (hourlyRate <= 50) return '$';
          if (hourlyRate <= 100) return '$$';
          if (hourlyRate <= 200) return '$$$';
          return '$$$$';
        };

        const transformedInternalVenues = internalVenuesData.map(profile => {
          // Clean address parsing
          const displayAddress = profile.venue_address || '';
          const addressParts = displayAddress.split(',').map(part => part.trim());
          const displayCity = addressParts.length > 1 ? addressParts[addressParts.length - 2] : '';
          const displayState = addressParts.length > 2 ? addressParts[addressParts.length - 1] : '';
          
          // Convert relative profile photo path to full Supabase URL
          const profilePhotoUrl = profile.profile_photo_url 
            ? `https://gmmfphwrwhvixldazcmp.supabase.co/storage/v1/object/public/profile-photos/${profile.profile_photo_url}`
            : '';
          
          return {
            id: `internal_${profile.user_id}`,
            venue_name: profile.venue_name || `${profile.first_name || profile.internal_users?.username || 'Unknown'} Venue`,
            address: displayAddress,
            city: displayCity,
            state_province: displayState,
            phone_number: profile.phone || '',
            website: '',
            operating_hours: profile.operating_hours || '',
            amenities: Array.isArray(profile.facilities_available) ? profile.facilities_available : [],
            sports_available: [],
            rating: undefined, // Will be loaded separately
            price_range: calculatePriceRange(profile.hourly_rates),
            description: profile.venue_description || profile.bio || 'Professional venue services',
            source: 'internal',
            slug: profile.internal_users?.username,
            profile_photo_url: profilePhotoUrl,
            hourly_rates: profile.hourly_rates
          } as Venue & { source: string; slug?: string };
        });
        allVenues = [...allVenues, ...transformedInternalVenues];
      }

      console.log('🏢 Combined venues:', allVenues);
      setVenues(allVenues);
      
      // Load ratings for all venues
      await loadVenueRatings(allVenues);
    } catch (error) {
      console.error('Error loading venues:', error);
      toast.error('Failed to load venues');
    } finally {
      setLoading(false);
    }
  };

  const loadVenueRatings = async (venueList: Venue[]) => {
    try {
      // Extract venue IDs - clean them for rating lookup
      const venueIds = venueList.map(venue => {
        // Remove prefixes to get the actual venue ID for rating lookup
        if (venue.id.startsWith('venue_')) {
          return venue.id.replace('venue_', '');
        } else if (venue.id.startsWith('internal_')) {
          return venue.id.replace('internal_', '');
        }
        return venue.id;
      });

      const ratings = await venueRatingsService.getVenueRatings(venueIds);
      
      // Map ratings back to the venue IDs with prefixes
      const mappedRatings: Record<string, VenueRating> = {};
      venueList.forEach(venue => {
        const cleanId = venue.id.startsWith('venue_') 
          ? venue.id.replace('venue_', '')
          : venue.id.startsWith('internal_') 
          ? venue.id.replace('internal_', '')
          : venue.id;
        
        if (ratings[cleanId]) {
          mappedRatings[venue.id] = ratings[cleanId];
        }
      });

      setRatingsMap(mappedRatings);
    } catch (error) {
      console.error('Error loading venue ratings:', error);
    }
  };

  const filteredVenues = venues.filter(venue => {
    const matchesSearch = !searchTerm || 
      venue.venue_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      venue.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      venue.sports_available?.some(sport => 
        sport.toLowerCase().includes(searchTerm.toLowerCase())
      );
    
    const matchesLocation = !locationFilter ||
      venue.city.toLowerCase().includes(locationFilter.toLowerCase()) ||
      venue.state_province.toLowerCase().includes(locationFilter.toLowerCase());

    return matchesSearch && matchesLocation;
  });

  const contactVenue = (venue: Venue) => {
    if (venue.phone_number) {
      window.open(`tel:${venue.phone_number}`, '_self');
    } else if (venue.website) {
      window.open(venue.website, '_blank');
    } else {
      toast.info('Contact information not available');
    }
  };

  const viewVenueDetails = (venue: Venue) => {
    // For internal venue users, navigate to their existing profile page
    if (venue.source === 'internal' && venue.slug) {
      navigate(`/${venue.slug}`);
    } else if (venue.source === 'venues') {
      navigate(`/venue/${venue.id}`);
    } else if (venue.website) {
      window.open(venue.website, '_blank');
    } else {
      toast.info('More details coming soon');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        {!isMobile && <Header />}
        {isMobile && <MobileHeader />}
        <main className={`${
          isMobile 
            ? 'pt-16 mb-16 py-4 px-4' 
            : 'mt-24 mb-6 py-6 md:py-12 px-4 md:px-6'
        }`}>
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sports-green"></div>
              <span className="ml-3 text-gray-400">Loading venues...</span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Desktop Header */}
      {!isMobile && <Header />}
      {/* Mobile Header */}
      {isMobile && <MobileHeader />}
      
      <main className={`${
        isMobile 
          ? 'pt-16 mb-16 py-4 px-4' 
          : 'mt-24 mb-6 py-6 md:py-12 px-4 md:px-6'
      }`}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-2">
                <MapPin className="h-8 w-8 text-sports-green" />
                Discover Venues
              </h1>
              <p className="text-gray-400 mt-2">
                Find the perfect venue for your next game or event
              </p>
            </div>
          </div>

          {/* Search and Filters */}
          <Card className="bg-gray-900 border-gray-700 mb-8">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search venues, sports, or amenities..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-gray-800 border-gray-600 text-white placeholder:text-gray-400"
                  />
                </div>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Filter by location..."
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="pl-10 bg-gray-800 border-gray-600 text-white placeholder:text-gray-400"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Venues List */}
          <Tabs defaultValue="all" className="space-y-4">
            <TabsList className="bg-gray-800 border-gray-700">
              <TabsTrigger value="all" className="data-[state=active]:bg-sports-green data-[state=active]:text-black">
                All Venues ({filteredVenues.length})
              </TabsTrigger>
              <TabsTrigger value="featured" className="data-[state=active]:bg-sports-green data-[state=active]:text-black">
                Featured
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="space-y-4">
              {filteredVenues.length === 0 ? (
                <div className="text-center py-12">
                  <MapPin className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-300 mb-2">No Venues Found</h3>
                  <p className="text-gray-500 mb-6">
                    {searchTerm || locationFilter ? 'No venues found matching your search' : 'No venues available at the moment'}
                  </p>
                </div>
              ) : (
                <VenuesGrid 
                  venues={filteredVenues} 
                  loading={loading}
                  onVenueContact={contactVenue}
                  onVenueDetails={viewVenueDetails}
                  ratingsMap={ratingsMap}
                />
              )}
            </TabsContent>

            <TabsContent value="featured" className="space-y-4">
              <Card className="bg-gray-900 border-gray-700">
                <CardContent className="p-8 text-center">
                  <Star className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Featured venues coming soon</p>
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </div>
      </main>
      
      {/* Desktop Footer */}
      {!isMobile && <Footer />}
      {/* Mobile Bottom Navigation */}
      {isMobile && <MobileBottomNav />}
    </div>
  );
}